import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Tabs,
  Button,
  Space,
  Table,
  InputNumber,
  Card,
  Modal,
  Upload,
  message,
  Tooltip,
  Popconfirm,
  Collapse,
  Input,
  DatePicker,
  Form,
  Spin,
  Empty,
  Tag,
  Select,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  PrinterOutlined,
  FileAddOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import {
  OilPricelist,
  OilPricelistItem,
  OilPricelistSummary,
  Competitor,
  getPricelists,
  getPricelist,
  createPricelist,
  updatePricelist,
  deletePricelist,
  addItem,
  updateItem,
  deleteItem,
  setCompetitorPrice,
  getCompetitors,
  createCompetitor,
  deleteCompetitor,
  importExcel,
  uploadSupplierFile,
  syncNevaPrices,
  activatePricelist,
  openPrintForm,
} from '../api/oilPricelists';
import BrandLogo from '../components/BrandLogo';
import PriceListTab from '../components/price-list/PriceListTab';

const { Title } = Typography;

// ── Helpers ────────────────────────────────────────────────────────────────────

const ceil5 = (x: number) => Math.ceil(x / 5) * 5;

const calcService = (base4l: number, vol: number): number => {
  if (!base4l || base4l <= 0) return 0;
  const perL = base4l / 4;
  if (vol === 5) return base4l;
  return Math.round(perL * vol * 100) / 100;
};

const fmtNum = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const formatNumber = (v: number | null | undefined): string => {
  if (v == null) return '—';
  return fmtNum.format(v);
};

const VOLUME_KEYS = ['1', '4', '5', '20', '60', '208'];

const PRICE_SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  override: { label: 'вручную', color: 'purple' },
  competitor: { label: 'конкурент', color: 'blue' },
  fallback: { label: 'наценка', color: 'orange' },
};

// Локальный пересчёт — зеркало backend calc_item (схема oil_pricelists.md §2)
const recalcItem = (item: OilPricelistItem, pl: OilPricelist): OilPricelistItem => {
  const prices = (item.competitor_prices || []).map(c => c.price);
  const minComp = prices.length ? Math.min(...prices) : null;

  const service = item.include_service ? calcService(pl.service_cost_4l, item.volume_liters) : 0;
  const purchase = item.neva_price ? item.neva_price * (1 - pl.neva_discount_pct / 100) : null;
  const cost = purchase != null ? purchase + service : null;

  let sale: number | null = null;
  let source: OilPricelistItem['price_source'] = null;
  let markupPct: number | null = null;

  if (item.sale_price_override != null) {
    sale = item.sale_price_override;
    source = 'override';
  } else if (minComp) {
    sale =
      pl.competitor_strategy === 'min_minus_pct' && pl.competitor_strategy_pct > 0
        ? ceil5(minComp * (1 - pl.competitor_strategy_pct / 100))
        : ceil5(minComp);
    source = 'competitor';
  } else if (purchase != null) {
    markupPct = item.markup_override;
    if (markupPct == null) {
      const catMarkups = pl.category_markups[item.category] || {};
      const vol = item.volume_liters;
      const volKey = vol === Math.floor(vol) ? String(Math.floor(vol)) : String(vol);
      markupPct = catMarkups[volKey] ?? 0;
    }
    const oilPrice = ceil5(purchase * (1 + markupPct / 100));
    sale = ceil5(oilPrice + service);
    source = 'fallback';
  }

  const margin = sale != null && cost != null ? Math.round((sale - cost) * 100) / 100 : null;
  const marginOk = margin != null ? margin >= (pl.min_margin || 0) : null;

  return {
    ...item,
    min_competitor_price: minComp,
    purchase_price: purchase != null ? Math.round(purchase * 100) / 100 : null,
    service_cost: Math.round(service * 100) / 100,
    cost_price: cost != null ? Math.round(cost * 100) / 100 : null,
    markup_pct: markupPct,
    sale_price: sale,
    price_source: source,
    margin,
    margin_ok: marginOk,
  };
};

const recalcAll = (items: OilPricelistItem[], pl: OilPricelist): OilPricelistItem[] =>
  items.map(it => recalcItem(it, pl));

// ── Compute rowSpan values ─────────────────────────────────────────────────────

interface RowSpanInfo {
  categorySpan: number;
  productSpan: number;
}

const computeRowSpans = (items: OilPricelistItem[]): Map<number, RowSpanInfo> => {
  const map = new Map<number, RowSpanInfo>();

  let i = 0;
  while (i < items.length) {
    const cat = items[i].category;
    let catEnd = i;
    while (catEnd < items.length && items[catEnd].category === cat) catEnd++;
    const catSpan = catEnd - i;

    let j = i;
    while (j < catEnd) {
      const prod = items[j].product_name;
      let prodEnd = j;
      while (prodEnd < catEnd && items[prodEnd].product_name === prod) prodEnd++;
      const prodSpan = prodEnd - j;

      for (let k = j; k < prodEnd; k++) {
        map.set(items[k].id, {
          categorySpan: k === i ? catSpan : 0,
          productSpan: k === j ? prodSpan : 0,
        });
      }
      j = prodEnd;
    }
    i = catEnd;
  }

  return map;
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  active: { label: 'Активный', color: 'green' },
  archived: { label: 'Архив', color: 'orange' },
};

// ── Component ──────────────────────────────────────────────────────────────────

const OilPricelistPage: React.FC = () => {
  const [pricelists, setPricelists] = useState<OilPricelistSummary[]>([]);
  const [activeBrand, setActiveBrand] = useState<string>('MOBIL');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pricelist, setPricelist] = useState<OilPricelist | null>(null);
  const [items, setItems] = useState<OilPricelistItem[]>([]);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCell, setEditingCell] = useState<{ id: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState<number | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createBrand, setCreateBrand] = useState<string>('MOBIL');
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [newCompetitorName, setNewCompetitorName] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const [createForm] = Form.useForm();
  const [addItemForm] = Form.useForm();

  const readOnly = pricelist?.status !== 'draft';

  // Группировка прайсов по бренду
  const brandMap = useMemo(() => {
    const map = new Map<string, OilPricelistSummary[]>();
    for (const pl of pricelists) {
      const brand = (pl.brand || 'Другие').toUpperCase();
      if (!map.has(brand)) map.set(brand, []);
      map.get(brand)!.push(pl);
    }
    return map;
  }, [pricelists]);

  const brands = useMemo(() => Array.from(brandMap.keys()).sort(), [brandMap]);

  const brandPricelists = useMemo(() => {
    const list = brandMap.get(activeBrand) || [];
    const order: Record<string, number> = { active: 0, draft: 1, archived: 2 };
    return [...list].sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3) || b.id - a.id);
  }, [brandMap, activeBrand]);

  // ── Load pricelists & competitors ──────────────────────────────────────────

  const loadPricelists = useCallback(async () => {
    try {
      const data = await getPricelists();
      setPricelists(data);
    } catch (e: any) {
      message.error('Ошибка загрузки прайсов: ' + (e.response?.data?.detail || e.message));
    }
  }, []);

  const loadCompetitors = useCallback(async () => {
    try {
      setCompetitors(await getCompetitors());
    } catch (e: any) {
      message.error('Ошибка загрузки конкурентов: ' + (e.response?.data?.detail || e.message));
    }
  }, []);

  useEffect(() => {
    loadPricelists();
    loadCompetitors();
  }, [loadPricelists, loadCompetitors]);

  // ── Load selected pricelist ────────────────────────────────────────────────

  const loadPricelist = useCallback(async (id: number) => {
    setLoading(true);
    try {
      const data = await getPricelist(id);
      setPricelist(data);
      setItems(recalcAll(data.items || [], data));
      setConfigDirty(false);
    } catch (e: any) {
      message.error('Ошибка загрузки прайса: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (brandPricelists.length === 0) {
      setSelectedId(null);
      setPricelist(null);
      setItems([]);
      return;
    }
    const current = brandPricelists.find(p => p.id === selectedId);
    const target = current || brandPricelists[0];
    if (target.id !== selectedId) setSelectedId(target.id);
    loadPricelist(target.id);
  }, [brandPricelists, selectedId, loadPricelist]);

  // ── Config save ────────────────────────────────────────────────────────────

  const saveConfig = useCallback(async () => {
    if (!pricelist) return;
    try {
      await updatePricelist(pricelist.id, {
        brand: pricelist.brand,
        valid_from: pricelist.valid_from,
        neva_discount_pct: pricelist.neva_discount_pct,
        service_cost_4l: pricelist.service_cost_4l,
        competitor_strategy: pricelist.competitor_strategy,
        competitor_strategy_pct: pricelist.competitor_strategy_pct,
        min_margin: pricelist.min_margin,
        category_markups: pricelist.category_markups,
      });
      setConfigDirty(false);
      message.success('Настройки сохранены');
    } catch (e: any) {
      message.error('Ошибка сохранения: ' + (e.response?.data?.detail || e.message));
    }
  }, [pricelist]);

  const updateConfigField = useCallback(
    (field: keyof OilPricelist, value: any) => {
      if (!pricelist) return;
      const updated = { ...pricelist, [field]: value };
      setPricelist(updated);
      setItems(recalcAll(items, updated));
      setConfigDirty(true);
    },
    [pricelist, items],
  );

  const updateMarkup = useCallback(
    (category: string, volKey: string, value: number | null) => {
      if (!pricelist) return;
      const newMarkups = { ...pricelist.category_markups };
      if (!newMarkups[category]) newMarkups[category] = {};
      newMarkups[category] = { ...newMarkups[category], [volKey]: value ?? 0 };
      const updated = { ...pricelist, category_markups: newMarkups };
      setPricelist(updated);
      setItems(recalcAll(items, updated));
      setConfigDirty(true);
    },
    [pricelist, items],
  );

  // ── Inline edit ────────────────────────────────────────────────────────────

  const startEdit = useCallback((id: number, field: string, currentValue: number | null) => {
    setEditingCell({ id, field });
    setEditValue(currentValue);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingCell || !pricelist) {
      setEditingCell(null);
      return;
    }

    const { id, field } = editingCell;
    const item = items.find(it => it.id === id);
    if (!item) {
      setEditingCell(null);
      return;
    }

    try {
      if (field.startsWith('comp_')) {
        const competitorId = Number(field.slice(5));
        await setCompetitorPrice(id, competitorId, editValue);
        setItems(prev =>
          prev.map(it => {
            if (it.id !== id) return it;
            const comp = competitors.find(c => c.id === competitorId);
            const rest = (it.competitor_prices || []).filter(cp => cp.competitor_id !== competitorId);
            const patched = {
              ...it,
              competitor_prices:
                editValue != null
                  ? [...rest, { competitor_id: competitorId, competitor_name: comp?.name ?? null, price: editValue, source: 'manual' }]
                  : rest,
            };
            return recalcItem(patched, pricelist);
          }),
        );
      } else {
        const payload: any = {};
        if (field === 'neva_price') payload.neva_price = editValue;
        else if (field === 'markup_pct') payload.markup_override = editValue;
        else if (field === 'sale_price_override') payload.sale_price_override = editValue;

        await updateItem(id, payload);
        setItems(prev =>
          prev.map(it => {
            if (it.id !== id) return it;
            const patched = { ...it };
            if (field === 'neva_price') patched.neva_price = editValue;
            else if (field === 'markup_pct') patched.markup_override = editValue;
            else if (field === 'sale_price_override') patched.sale_price_override = editValue;
            return recalcItem(patched, pricelist);
          }),
        );
      }
    } catch (e: any) {
      message.error('Ошибка сохранения: ' + (e.response?.data?.detail || e.message));
    }

    setEditingCell(null);
  }, [editingCell, editValue, items, pricelist, competitors]);

  // ── Create pricelist ───────────────────────────────────────────────────────

  const handleCreate = useCallback(async () => {
    try {
      const values = await createForm.validateFields();
      const created = await createPricelist({
        brand: values.brand || createBrand,
        valid_from: values.valid_from ? values.valid_from.format('YYYY-MM-DD') : null,
        neva_discount_pct: values.neva_discount_pct ?? 19,
        service_cost_4l: values.service_cost_4l ?? 850,
        category_markups: {},
      });
      message.success('Черновик прайса создан');
      setCreateModalOpen(false);
      createForm.resetFields();
      await loadPricelists();
      setActiveBrand((created.brand || 'MOBIL').toUpperCase());
      setSelectedId(created.id);
    } catch (e: any) {
      if (e.errorFields) return;
      message.error('Ошибка создания: ' + (e.response?.data?.detail || e.message));
    }
  }, [createForm, loadPricelists, createBrand]);

  // ── Delete pricelist ───────────────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!pricelist) return;
    try {
      await deletePricelist(pricelist.id);
      message.success('Прайс удалён');
      setPricelist(null);
      setItems([]);
      setSelectedId(null);
      loadPricelists();
    } catch (e: any) {
      message.error('Ошибка удаления: ' + (e.response?.data?.detail || e.message));
    }
  }, [pricelist, loadPricelists]);

  // ── Add item ───────────────────────────────────────────────────────────────

  const handleAddItem = useCallback(async () => {
    if (!pricelist) return;
    try {
      const values = await addItemForm.validateFields();
      const newItem = await addItem(pricelist.id, {
        category: values.category,
        product_name: values.product_name,
        specs: values.specs || null,
        volume_liters: values.volume_liters,
        neva_price: values.neva_price ?? null,
        include_service: values.include_service ?? true,
        sort_order: items.length,
      });
      setItems(prev => [...prev, recalcItem(newItem, pricelist)]);
      setAddItemModalOpen(false);
      addItemForm.resetFields();
      message.success('Позиция добавлена');
    } catch (e: any) {
      if (e.errorFields) return;
      message.error('Ошибка: ' + (e.response?.data?.detail || e.message));
    }
  }, [pricelist, addItemForm, items]);

  // ── Delete item ────────────────────────────────────────────────────────────

  const handleDeleteItem = useCallback(async (id: number) => {
    try {
      await deleteItem(id);
      setItems(prev => prev.filter(it => it.id !== id));
      message.success('Удалено');
    } catch (e: any) {
      message.error('Ошибка удаления: ' + (e.response?.data?.detail || e.message));
    }
  }, []);

  // ── Competitors dictionary ─────────────────────────────────────────────────

  const handleAddCompetitor = useCallback(async () => {
    const name = newCompetitorName.trim();
    if (!name) return;
    try {
      await createCompetitor(name);
      setNewCompetitorName('');
      loadCompetitors();
      message.success(`Конкурент «${name}» добавлен`);
    } catch (e: any) {
      message.error('Ошибка: ' + (e.response?.data?.detail || e.message));
    }
  }, [newCompetitorName, loadCompetitors]);

  const handleDeleteCompetitor = useCallback(
    async (id: number) => {
      try {
        await deleteCompetitor(id);
        loadCompetitors();
        if (pricelist) loadPricelist(pricelist.id);
      } catch (e: any) {
        message.error('Ошибка удаления: ' + (e.response?.data?.detail || e.message));
      }
    },
    [loadCompetitors, loadPricelist, pricelist],
  );

  // ── Sync Neva prices ───────────────────────────────────────────────────────

  const handleSyncNeva = useCallback(async () => {
    if (!pricelist) return;
    setLoading(true);
    try {
      const result = await syncNevaPrices(pricelist.id);
      message.success(`Обновлено цен: ${result.updated}`);
      loadPricelist(pricelist.id);
    } catch (e: any) {
      message.error('Ошибка синхронизации: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [pricelist, loadPricelist]);

  // ── Activate pricelist ─────────────────────────────────────────────────────

  const handleActivate = useCallback(async () => {
    if (!pricelist) return;
    try {
      const result = await activatePricelist(pricelist.id);
      message.success(
        `Прайс утверждён. Розничных цен создано: ${result.retail_prices_created}, закрыто: ${result.retail_prices_updated}. Печатная форма — в Документах.`,
      );
      loadPricelist(pricelist.id);
      loadPricelists();
    } catch (e: any) {
      message.error('Ошибка утверждения: ' + (e.response?.data?.detail || e.message));
    }
  }, [pricelist, loadPricelist, loadPricelists]);

  // ── Import Excel ───────────────────────────────────────────────────────────

  const uploadProps: UploadProps = useMemo(
    () => ({
      accept: '.xlsx,.xls',
      showUploadList: false,
      beforeUpload: async (file: File) => {
        if (!pricelist) {
          message.warning('Сначала выберите прайс');
          return Upload.LIST_IGNORE;
        }
        try {
          const result = await importExcel(pricelist.id, file);
          message.success(`Импортировано: ${result.items_imported ?? 0} позиций`);
          loadPricelist(pricelist.id);
        } catch (e: any) {
          message.error('Ошибка импорта: ' + (e.response?.data?.detail || e.message));
        }
        return Upload.LIST_IGNORE;
      },
    }),
    [pricelist, loadPricelist],
  );

  const supplierFileProps: UploadProps = useMemo(
    () => ({
      accept: '.xlsx,.xls,.csv,.pdf,.jpg,.jpeg,.png',
      showUploadList: false,
      beforeUpload: async (file: File) => {
        if (!pricelist) return Upload.LIST_IGNORE;
        try {
          await uploadSupplierFile(pricelist.id, file);
          message.success('Файл поставщика сохранён в Документах');
          loadPricelist(pricelist.id);
          loadPricelists();
        } catch (e: any) {
          message.error('Ошибка загрузки: ' + (e.response?.data?.detail || e.message));
        }
        return Upload.LIST_IGNORE;
      },
    }),
    [pricelist, loadPricelist, loadPricelists],
  );

  // ── Row spans ──────────────────────────────────────────────────────────────

  const rowSpans = useMemo(() => computeRowSpans(items), [items]);

  // ── Editable cell renderer ─────────────────────────────────────────────────

  const renderEditableCell = useCallback(
    (value: number | null, record: OilPricelistItem, field: string) => {
      if (readOnly) {
        return <span>{formatNumber(value)}</span>;
      }
      const isEditing = editingCell?.id === record.id && editingCell?.field === field;

      if (isEditing) {
        return (
          <InputNumber
            ref={inputRef as any}
            size="small"
            value={editValue}
            onChange={v => setEditValue(v)}
            onPressEnter={saveEdit}
            onBlur={saveEdit}
            style={{ width: '100%' }}
            controls={false}
          />
        );
      }

      return (
        <div
          onClick={() => startEdit(record.id, field, value)}
          style={{
            cursor: 'pointer',
            padding: '2px 4px',
            borderRadius: 3,
            background: '#fafafa',
            border: '1px solid #e8e8e8',
            minHeight: 22,
            lineHeight: '22px',
            textAlign: 'right',
          }}
        >
          {formatNumber(value)}
        </div>
      );
    },
    [readOnly, editingCell, editValue, saveEdit, startEdit],
  );

  // ── Table columns ──────────────────────────────────────────────────────────

  const columns: ColumnsType<OilPricelistItem> = useMemo(() => {
    const cols: ColumnsType<OilPricelistItem> = [
      {
        title: 'Категория',
        dataIndex: 'category',
        key: 'category',
        width: 110,
        fixed: 'left' as const,
        onCell: record => ({
          rowSpan: rowSpans.get(record.id)?.categorySpan ?? 1,
          style: { verticalAlign: 'top', fontWeight: 500, background: '#fafafa' },
        }),
        render: (val: string) => val,
      },
      {
        title: 'Товар',
        dataIndex: 'product_name',
        key: 'product_name',
        width: 230,
        fixed: 'left' as const,
        onCell: record => ({
          rowSpan: rowSpans.get(record.id)?.productSpan ?? 1,
          style: { verticalAlign: 'top' },
        }),
        render: (val: string, record) => (
          <Tooltip title={record.specs || undefined}>
            <Space>
              {record.catalog?.brand && <BrandLogo brand={record.catalog.brand} size="0.9em" />}
              <span>{val}</span>
              {record.catalog?.viscosity && <Tag style={{ fontSize: 11 }}>{record.catalog.viscosity}</Tag>}
            </Space>
          </Tooltip>
        ),
      },
      {
        title: 'Объем',
        dataIndex: 'volume_liters',
        key: 'volume_liters',
        width: 65,
        align: 'center' as const,
        render: (v: number) => `${v}л`,
      },
      {
        title: 'Цена НЕВА',
        dataIndex: 'neva_price',
        key: 'neva_price',
        width: 95,
        align: 'right' as const,
        render: (val: number | null, record) => renderEditableCell(val, record, 'neva_price'),
      },
      {
        title: 'Закупка',
        dataIndex: 'purchase_price',
        key: 'purchase_price',
        width: 90,
        align: 'right' as const,
        render: (v: number | null) => formatNumber(v),
      },
      {
        title: 'Замена',
        dataIndex: 'service_cost',
        key: 'service_cost',
        width: 75,
        align: 'right' as const,
        render: (v: number | null) => (v ? formatNumber(v) : '—'),
      },
      {
        title: 'Себест.',
        dataIndex: 'cost_price',
        key: 'cost_price',
        width: 90,
        align: 'right' as const,
        render: (v: number | null) => formatNumber(v),
      },
    ];

    // Динамические колонки конкурентов
    for (const comp of competitors) {
      cols.push({
        title: comp.name,
        key: `comp_${comp.id}`,
        width: 95,
        align: 'right' as const,
        render: (_: any, record: OilPricelistItem) => {
          const cp = (record.competitor_prices || []).find(p => p.competitor_id === comp.id);
          const isMin =
            cp != null && record.min_competitor_price != null && cp.price === record.min_competitor_price;
          const cell = renderEditableCell(cp?.price ?? null, record, `comp_${comp.id}`);
          return isMin ? <span style={{ background: '#e6f4ff', display: 'block' }}>{cell}</span> : cell;
        },
      });
    }

    cols.push(
      {
        title: 'Мин. конкур.',
        dataIndex: 'min_competitor_price',
        key: 'min_competitor_price',
        width: 95,
        align: 'right' as const,
        render: (v: number | null) => (v != null ? <b>{formatNumber(v)}</b> : '—'),
      },
      {
        title: 'Наценка %',
        dataIndex: 'markup_pct',
        key: 'markup_pct',
        width: 80,
        align: 'center' as const,
        render: (val: number | null, record) => {
          const effective = record.markup_override ?? val;
          return renderEditableCell(effective, record, 'markup_pct');
        },
      },
      {
        title: 'Ручная цена',
        dataIndex: 'sale_price_override',
        key: 'sale_price_override',
        width: 95,
        align: 'right' as const,
        render: (val: number | null, record) => renderEditableCell(val, record, 'sale_price_override'),
      },
      {
        title: 'Продажа',
        dataIndex: 'sale_price',
        key: 'sale_price',
        width: 130,
        align: 'right' as const,
        render: (v: number | null, record) => {
          const src = record.price_source ? PRICE_SOURCE_LABELS[record.price_source] : null;
          return (
            <Space size={4}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{formatNumber(v)}</span>
              {src && (
                <Tag color={src.color} style={{ fontSize: 10, marginInlineEnd: 0 }}>
                  {src.label}
                </Tag>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Маржа',
        dataIndex: 'margin',
        key: 'margin',
        width: 90,
        align: 'right' as const,
        render: (v: number | null, record) => {
          if (v == null) return '—';
          const bad = record.margin_ok === false;
          return (
            <span style={{ color: bad ? '#f5222d' : '#52c41a', fontWeight: 500 }}>
              {formatNumber(v)}
            </span>
          );
        },
      },
    );

    if (!readOnly) {
      cols.push({
        title: '',
        key: 'actions',
        width: 40,
        fixed: 'right' as const,
        render: (_: any, record: OilPricelistItem) => (
          <Popconfirm
            title="Удалить позицию?"
            onConfirm={() => handleDeleteItem(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
      });
    }

    return cols;
  }, [rowSpans, renderEditableCell, handleDeleteItem, competitors, readOnly]);

  // ── Category markups table ─────────────────────────────────────────────────

  const categoryMarkupsContent = useMemo(() => {
    if (!pricelist) return null;
    const categories = pricelist.categories || Object.keys(pricelist.category_markups || {});
    if (categories.length === 0) {
      return <Empty description="Нет категорий. Импортируйте данные." />;
    }

    const markupCols: ColumnsType<{ category: string }> = [
      {
        title: 'Категория',
        dataIndex: 'category',
        key: 'category',
        width: 200,
        fixed: 'left' as const,
      },
      ...VOLUME_KEYS.map(vol => ({
        title: `${vol}л`,
        key: `vol_${vol}`,
        width: 80,
        align: 'center' as const,
        render: (_: any, row: { category: string }) => {
          const val = pricelist.category_markups?.[row.category]?.[vol] ?? 0;
          return (
            <InputNumber
              size="small"
              value={val}
              onChange={v => updateMarkup(row.category, vol, v)}
              style={{ width: 60 }}
              controls={false}
              suffix="%"
              disabled={readOnly}
            />
          );
        },
      })),
    ];

    const markupData = categories.map(c => ({ category: c, key: c }));

    return (
      <Table
        columns={markupCols}
        dataSource={markupData}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 700 }}
      />
    );
  }, [pricelist, updateMarkup, readOnly]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const st = pricelist ? STATUS_LABELS[pricelist.status] : null;

  return (
    <div>
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Прайс масло
        </Title>
        <Space wrap>
          <Button icon={<PlusOutlined />} onClick={() => { setCreateBrand('MOBIL'); setCreateModalOpen(true); }}>
            Создать Mobil
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => { setCreateBrand('ZIC'); setCreateModalOpen(true); }}>
            Создать ZIC
          </Button>
          {pricelist && !readOnly && (
            <>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Импорт Excel</Button>
              </Upload>
              <Upload {...supplierFileProps}>
                <Button icon={<FileAddOutlined />}>
                  Файл поставщика{pricelist.supplier_document_id ? ' ✓' : ''}
                </Button>
              </Upload>
              <Button icon={<DollarOutlined />} onClick={handleSyncNeva} loading={loading}>
                Цены НЕВА
              </Button>
            </>
          )}
          {pricelist && (
            <>
              <Button icon={<ReloadOutlined />} onClick={() => loadPricelist(pricelist.id)}>
                Обновить
              </Button>
              <Button icon={<PrinterOutlined />} onClick={() => openPrintForm(pricelist.id)}>
                Печать
              </Button>
            </>
          )}
          {pricelist && pricelist.status === 'draft' && (
            <Popconfirm
              title="Утвердить прайс?"
              description="Текущий активный прайс бренда уйдёт в архив, розничные цены будут пересозданы."
              onConfirm={handleActivate}
              okText="Утвердить"
              cancelText="Отмена"
            >
              <Button type="primary" icon={<CheckCircleOutlined />}>
                Утвердить
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      {/* Brand tabs */}
      <Tabs
        activeKey={activeBrand}
        onChange={setActiveBrand}
        style={{ marginBottom: 8 }}
        items={[
          {
            key: 'MOBIL',
            label: (
              <Space>
                <BrandLogo brand="Mobil" size="1em" />
                <span>Mobil</span>
              </Space>
            ),
          },
          {
            key: 'ZIC',
            label: (
              <Space>
                <span style={{ fontWeight: 600 }}>ZIC</span>
              </Space>
            ),
          },
          {
            key: 'FIXED',
            label: (
              <Space>
                <span style={{ fontWeight: 600 }}>Клиентский прайс</span>
              </Space>
            ),
          },
          ...brands
            .filter(b => b !== 'MOBIL' && b !== 'ZIC')
            .map(b => ({ key: b, label: b })),
        ]}
      />

      {/* Pricelist selector */}
      {brandPricelists.length > 0 && (
        <Space style={{ marginBottom: 12 }} wrap>
          <Select
            value={selectedId}
            onChange={setSelectedId}
            style={{ minWidth: 320 }}
            options={brandPricelists.map(p => ({
              value: p.id,
              label: `№${p.id} — ${STATUS_LABELS[p.status]?.label || p.status}${p.valid_from ? `, с ${dayjs(p.valid_from).format('DD.MM.YYYY')}` : ''}${p.valid_to ? ` по ${dayjs(p.valid_to).format('DD.MM.YYYY')}` : ''} (${p.item_count} поз.)`,
            }))}
          />
          {st && <Tag color={st.color}>{st.label}</Tag>}
          {pricelist?.document_id && <Tag color="blue">печатная форма в Документах</Tag>}
        </Space>
      )}

      {/* Config card */}
      {pricelist && (
        <Collapse
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'config',
              label: (
                <Space>
                  <span>Настройки прайса</span>
                  <Tag>{pricelist.brand}</Tag>
                  {st && <Tag color={st.color}>{st.label}</Tag>}
                  {configDirty && <Tag color="orange">не сохранено</Tag>}
                </Space>
              ),
              children: (
                <div>
                  <Space wrap size="large" style={{ marginBottom: 16 }}>
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Бренд</div>
                      <Input
                        value={pricelist.brand}
                        onChange={e => updateConfigField('brand', e.target.value)}
                        style={{ width: 150 }}
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Дата начала</div>
                      <DatePicker
                        value={pricelist.valid_from ? dayjs(pricelist.valid_from) : null}
                        onChange={d => updateConfigField('valid_from', d ? d.format('YYYY-MM-DD') : null)}
                        format="DD.MM.YYYY"
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Скидка НЕВА, %</div>
                      <InputNumber
                        value={pricelist.neva_discount_pct}
                        onChange={v => updateConfigField('neva_discount_pct', v ?? 0)}
                        style={{ width: 100 }}
                        min={0}
                        max={100}
                        suffix="%"
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Замена 4л, руб.</div>
                      <InputNumber
                        value={pricelist.service_cost_4l}
                        onChange={v => updateConfigField('service_cost_4l', v ?? 0)}
                        style={{ width: 120 }}
                        min={0}
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Стратегия цены</div>
                      <Select
                        value={pricelist.competitor_strategy}
                        onChange={v => updateConfigField('competitor_strategy', v)}
                        style={{ width: 220 }}
                        disabled={readOnly}
                        options={[
                          { value: 'min', label: 'Минимум конкурентов' },
                          { value: 'min_minus_pct', label: 'Минимум минус %' },
                        ]}
                      />
                    </div>
                    {pricelist.competitor_strategy === 'min_minus_pct' && (
                      <div>
                        <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Минус, %</div>
                        <InputNumber
                          value={pricelist.competitor_strategy_pct}
                          onChange={v => updateConfigField('competitor_strategy_pct', v ?? 0)}
                          style={{ width: 100 }}
                          min={0}
                          max={50}
                          suffix="%"
                          disabled={readOnly}
                        />
                      </div>
                    )}
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Мин. маржа, ₽</div>
                      <InputNumber
                        value={pricelist.min_margin}
                        onChange={v => updateConfigField('min_margin', v ?? 0)}
                        style={{ width: 110 }}
                        disabled={readOnly}
                      />
                    </div>
                  </Space>

                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    Конкуренты (справочник)
                  </div>
                  <Space wrap style={{ marginBottom: 16 }}>
                    {competitors.map(c => (
                      <Tag
                        key={c.id}
                        closable
                        onClose={e => {
                          e.preventDefault();
                          Modal.confirm({
                            title: `Удалить конкурента «${c.name}»?`,
                            content: 'Все его цены в прайсах будут удалены.',
                            okText: 'Удалить',
                            okButtonProps: { danger: true },
                            cancelText: 'Отмена',
                            onOk: () => handleDeleteCompetitor(c.id),
                          });
                        }}
                      >
                        {c.name}
                      </Tag>
                    ))}
                    <Input
                      size="small"
                      placeholder="Новый конкурент"
                      value={newCompetitorName}
                      onChange={e => setNewCompetitorName(e.target.value)}
                      onPressEnter={handleAddCompetitor}
                      style={{ width: 160 }}
                    />
                    <Button size="small" onClick={handleAddCompetitor} disabled={!newCompetitorName.trim()}>
                      Добавить
                    </Button>
                  </Space>

                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    Fallback-наценки по категориям (если нет цен конкурентов)
                  </div>
                  {categoryMarkupsContent}

                  {!readOnly && (
                    <div style={{ marginTop: 16 }}>
                      <Space>
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          onClick={saveConfig}
                          disabled={!configDirty}
                        >
                          Сохранить настройки
                        </Button>
                        <Popconfirm
                          title="Удалить прайс?"
                          description="Все позиции будут удалены"
                          onConfirm={handleDelete}
                          okText="Удалить"
                          cancelText="Отмена"
                          okButtonProps={{ danger: true }}
                        >
                          <Button danger icon={<DeleteOutlined />}>
                            Удалить прайс
                          </Button>
                        </Popconfirm>
                      </Space>
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Main table */}
      {activeBrand === 'FIXED' ? (
        <PriceListTab />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : !pricelist ? (
        <Card>
          <Empty
            description={
              activeBrand === 'ZIC'
                ? 'Прайс ZIC ещё не создан. Нажмите «Создать ZIC» вверху.'
                : 'Прайс Mobil не найден. Нажмите «Создать Mobil» вверху.'
            }
          />
        </Card>
      ) : (
        <>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography.Text type="secondary">Позиций: {items.length}</Typography.Text>
            {!readOnly && (
              <Button size="small" icon={<PlusOutlined />} onClick={() => setAddItemModalOpen(true)}>
                Добавить позицию
              </Button>
            )}
          </div>
          <Table
            columns={columns}
            dataSource={items}
            rowKey="id"
            size="small"
            bordered
            pagination={false}
            scroll={{ x: 1800, y: 'calc(100vh - 340px)' }}
            sticky
            rowClassName={record => (record.margin_ok === false ? 'row-margin-bad' : '')}
          />
          <style>{`.row-margin-bad td { background: #fff1f0 !important; }`}</style>
        </>
      )}

      {/* Create pricelist modal */}
      <Modal
        title={`Создать прайс ${createBrand}`}
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        okText="Создать"
        cancelText="Отмена"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="brand"
            label="Бренд"
            rules={[{ required: true, message: 'Укажите бренд' }]}
            initialValue={createBrand}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item name="valid_from" label="Дата начала">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="neva_discount_pct" label="Скидка НЕВА, %" initialValue={19}>
            <InputNumber style={{ width: '100%' }} min={0} max={100} />
          </Form.Item>
          <Form.Item name="service_cost_4l" label="Стоимость замены 4л, руб." initialValue={850}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add item modal */}
      <Modal
        title="Добавить позицию"
        open={addItemModalOpen}
        onOk={handleAddItem}
        onCancel={() => {
          setAddItemModalOpen(false);
          addItemForm.resetFields();
        }}
        okText="Добавить"
        cancelText="Отмена"
      >
        <Form form={addItemForm} layout="vertical">
          <Form.Item
            name="category"
            label="Категория"
            rules={[{ required: true, message: 'Укажите категорию' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="product_name"
            label="Наименование товара"
            rules={[{ required: true, message: 'Укажите наименование' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="specs" label="Спецификация">
            <Input />
          </Form.Item>
          <Form.Item
            name="volume_liters"
            label="Объём, л"
            rules={[{ required: true, message: 'Укажите объём' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0.1} />
          </Form.Item>
          <Form.Item name="neva_price" label="Цена НЕВА">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item
            name="include_service"
            label="Включить замену"
            initialValue={true}
          >
            <Select
              options={[
                { value: true, label: 'Да' },
                { value: false, label: 'Нет' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OilPricelistPage;
